import { PromptConfirmedEvent, SubmitDataEvent } from '@ui-common/state'
import { assign, fromPromise, setup } from 'xstate'

const INITIAL = 'initial'
const SUBMITTING = 'submitting'
const SUBMIT_FAILED = 'submitFailed'
const SUBMIT_COMPLETED = 'submitCompleted'

type CreateApplicationMachineEvent<TNewApplicationEndpointRequest> =
  | SubmitDataEvent<TNewApplicationEndpointRequest>
  | PromptConfirmedEvent

interface CreateApplicationMachineContext<TNewApplicationEndpointResponse> {
  response?: TNewApplicationEndpointResponse
  error?: unknown
}

export type NewApplicationMachineType<TNewApplicationEndpointRequest, TNewApplicationEndpointResponse> = ReturnType<
  typeof createNewApplicationMachine<TNewApplicationEndpointRequest, TNewApplicationEndpointResponse>
>

export const createNewApplicationMachine = <TNewApplicationEndpointRequest, TNewApplicationEndpointResponse>(
  machineId: string,
  createApplicationCallback: (
    requestPayload: TNewApplicationEndpointRequest,
  ) => Promise<TNewApplicationEndpointResponse>,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
) => {
  return setup({
    types: {
      context: {} as CreateApplicationMachineContext<TNewApplicationEndpointResponse>,
      events: {} as CreateApplicationMachineEvent<TNewApplicationEndpointRequest>,
    },
    actions: {
      proceedWithNewApplication: assign(({ context: _context }, data: TNewApplicationEndpointResponse) => {
        return {
          response: data,
        }
      }),
      saveError: assign((_, error?: Error) => ({ error })),
      clearError: assign({ error: undefined }),
    },
    actors: {
      createApplication: fromPromise<TNewApplicationEndpointResponse, SubmitDataEvent<TNewApplicationEndpointRequest>>(
        ({ input }) => createApplicationCallback(input.data),
      ),
    },
  }).createMachine({
    id: machineId,
    context: {},
    initial: INITIAL,
    states: {
      [INITIAL]: {
        entry: { type: 'clearError' },
        on: {
          SUBMIT: [
            {
              target: SUBMITTING,
            },
          ],
        },
      },
      [SUBMITTING]: {
        invoke: {
          id: 'createApplication',
          input: ({ event }) => event as SubmitDataEvent<TNewApplicationEndpointRequest>,
          src: 'createApplication',
          onDone: {
            target: SUBMIT_COMPLETED,
            actions: {
              type: 'proceedWithNewApplication',
              params: ({ event }) => event.output,
            },
          },
          onError: {
            target: SUBMIT_FAILED,
            actions: { type: 'saveError', params: ({ event }) => event.error as Error },
          },
        },
      },
      [SUBMIT_FAILED]: {
        on: {
          PROMPT_CONFIRMED: INITIAL,
        },
      },
      [SUBMIT_COMPLETED]: {
        type: 'final',
      },
    },
  })
}
